import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, Info, Loader2, Pencil, Phone, PhoneCall, PhoneForwarded, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { CallLogs, ExistingNumberForm, PhoneNumberDisplay, ProvisioningForm } from "@/components/phone";
import ForwardingSetup from "@/components/phone/ForwardingSetup";
import PhoneDestinationForm, { PhoneDestinationFormValues } from "@/components/phone/PhoneDestinationForm";
import { useUpdateAgent } from "@/hooks/agent";
import { useRole } from "@/hooks/auth/useRole";
import { useBilledPhoneProvisioning, useOutboundCall, usePhoneNumber } from "@/hooks/phone";
import type { BilledProvisionParams } from "@/hooks/phone/useBilledPhoneProvisioning";
import { Agent } from "@/types/agent/agent";
import { PhoneCheckKind } from "@/types/agent/brand-integrity";
import { getPhoneCheckPath } from "@/utils/agent/phoneCheck";

const MAX_PHONE_DESTINATIONS = 10;

interface PhoneNumberModalProps {
  genie: Agent;
  isOpen: boolean;
  onClose: () => void;
  onOperationComplete?: () => void | Promise<void>;
}

const PhoneNumberModal = ({ genie, isOpen, onClose, onOperationComplete }: PhoneNumberModalProps) => {
  const navigate = useNavigate();
  const brand = genie.branding?.primaryColor ?? "#4E9CFF";
  const [activeTab, setActiveTab] = useState<"phone-numbers" | "escalation" | "call-logs">("phone-numbers");
  const [showProvisionForm, setShowProvisionForm] = useState(false);
  const [showExistingNumberForm, setShowExistingNumberForm] = useState(false);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [editingTransferId, setEditingTransferId] = useState<string | null>(null);
  const [releasingTransferId, setReleasingTransferId] = useState<string | null>(null);
  const [showEscalationForm, setShowEscalationForm] = useState(false);
  const [editingEscalationId, setEditingEscalationId] = useState<string | null>(null);
  const [releasingEscalationId, setReleasingEscalationId] = useState<string | null>(null);
  const [showPhoneInfo, setShowPhoneInfo] = useState(false);
  const [showPhoneTypePrompt, setShowPhoneTypePrompt] = useState(false);
  const [showTransferInfo, setShowTransferInfo] = useState(false);
  const [showDestinationGuide, setShowDestinationGuide] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  /** The customer chose "keep my number" — open forwarding once a number exists. */
  const [forwardingIntent, setForwardingIntent] = useState(false);
  const [showForwarding, setShowForwarding] = useState(false);
  const [isTogglingVoiceBridge, setIsTogglingVoiceBridge] = useState(false);
  const [isTogglingShowButton, setIsTogglingShowButton] = useState(false);

  const updateAgent = useUpdateAgent();
  const { isInternalAdmin } = useRole();
  const {
    provisionWithBilling,
    releaseWithBilling,
    canAddPhone,
    isProvisioning: isProvisioningBilled,
    isReleasing,
  } = useBilledPhoneProvisioning(genie);

  const {
    release,
    provisionTransfer,
    updateTransfer,
    provisionEscalation,
    updateEscalation,
    releaseEscalation,
    requestExistingNumberAttachment,
    isProvisioningTransfer,
    isUpdatingTransfer,
    isProvisioningEscalation,
    isUpdatingEscalation,
    isRequestingExistingNumberAttachment,
  } = usePhoneNumber(genie);

  const phoneNumber = genie.phone
    ? {
        id: genie.phone.twilio_sid || genie.phone.id,
        elevenlabsId: genie.phone.elevenlabs_id,
        phoneNumber: genie.phone.number || "",
        friendlyName: genie.phone.friendly_name,
        status: genie.phone.status || "",
        dateCreated: genie.phone.created_at || "",
        recordId: genie.phone.id,
        metadata: genie.phone.metadata,
      }
    : null;

  const transferPhones = (genie.transfer_phones ?? (genie.transfer_phone ? [genie.transfer_phone] : [])).map((phone) => ({
    id: phone.twilio_sid || phone.id,
    phoneNumber: phone.number || "",
    friendlyName: phone.friendly_name || "",
    description: phone.metadata?.description || "",
    status: phone.status || "",
  }));
  const transferPhone = transferPhones[0] ?? null;
  const canAddTransfer = transferPhones.length < MAX_PHONE_DESTINATIONS;

  const escalationPhones = (genie.escalation_phones ?? (genie.escalation_phone ? [genie.escalation_phone] : [])).map(
    (phone) => ({
      id: phone.twilio_sid || phone.id,
      phoneNumber: phone.number || "",
      friendlyName: phone.friendly_name || "",
      description: phone.metadata?.description || "",
    }),
  );
  const canAddEscalation = escalationPhones.length < MAX_PHONE_DESTINATIONS;

  const hasDestinations = transferPhones.length > 0 || escalationPhones.length > 0;

  // How someone on each channel reaches a person with the current setup.
  const visitorHandoffSummary = !genie.voice_bridge_enabled
    ? "Live Handoff from Browser is off, so website visitors can't be connected to a person."
    : escalationPhones.length > 0
      ? "Website visitors who ask for a person get a Connect me button that calls your escalation numbers."
      : "Website visitors who ask for a person get a Connect me button that calls your transfer numbers.";
  const callerHandoffSummary =
    transferPhones.length > 0
      ? "Phone callers who ask for a person are transferred to your transfer numbers."
      : "Phone callers can't be transferred until you add a transfer number.";

  const destinationSummary = [
    {
      label: "Transfer numbers: for phone callers",
      phones: transferPhones,
      emptyText: "None added.",
    },
    {
      label: "Escalation numbers: for website visitors",
      phones: escalationPhones,
      emptyText: genie.voice_bridge_enabled ? "None added. Live handoff uses your transfer numbers." : "None added.",
    },
  ];

  const configuredTransferNumbers = new Set(
    (genie.settings?.conversation_config?.agent?.prompt?.built_in_tools?.transfer_to_number?.params?.transfers ?? [])
      .map((transfer) => (transfer.transfer_destination?.type === "phone" ? transfer.transfer_destination.phone_number : null))
      .filter(Boolean),
  );
  const isTransferConfigured = transferPhones.every((phone) => configuredTransferNumbers.has(phone.phoneNumber));

  const { makeCall, isCalling, error: callError } = useOutboundCall();

  // The server attaches whichever tool the genie uses to start the handoff
  // (handle-agent's syncHumanHandoffTool), so switching it on is all it takes.
  const enableLiveHandoff = async () => {
    if (genie.voice_bridge_enabled) return;
    await updateAgent.mutateAsync({ agent: genie, updateData: { voice_bridge_enabled: true } });
  };

  const handleProvisionTransfer = async ({ enableLiveHandoff: connectVisitors, ...values }: PhoneDestinationFormValues) => {
    try {
      await provisionTransfer({ ...values, agentId: genie.id });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Oops, an error occurred");
      console.error("Error provisioning transfer number:", error);
      return;
    }

    setShowTransferForm(false);

    if (connectVisitors) {
      try {
        await enableLiveHandoff();
      } catch (error) {
        toast.error(
          "The transfer number was saved, but Live Handoff from Browser couldn't be switched on. Switch it on below.",
        );
        console.error("Error switching on live handoff:", error);
      }
    }

    await onOperationComplete?.();
  };

  const handleUpdateTransfer = async (phoneNumberId: string, values: PhoneDestinationFormValues) => {
    try {
      await updateTransfer({ phoneNumberId, friendlyName: values.friendlyName, description: values.description });
      setEditingTransferId(null);
      await onOperationComplete?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Oops, an error occurred");
      console.error("Error updating transfer number:", error);
    }
  };

  const handleResyncTransfers = async () => {
    if (!transferPhone) return;
    try {
      await updateTransfer({ phoneNumberId: transferPhone.id });
      await onOperationComplete?.();
    } catch (error) {
      toast.error("Oops, an error occurred");
      console.error("Error syncing transfer numbers:", error);
    }
  };

  const handleReleaseTransfer = async (phoneNumberId: string) => {
    if (!confirm("Are you sure you want to release this transfer phone number? This action cannot be undone.")) return;
    setReleasingTransferId(phoneNumberId);
    try {
      await release(phoneNumberId);

      // Without escalation numbers the handoff dials the transfer numbers, so
      // it goes off with the last of them rather than offer a call to no one.
      const hasRemainingDestination = transferPhones.length > 1 || escalationPhones.length > 0;
      if (genie.voice_bridge_enabled && !hasRemainingDestination) {
        await updateAgent.mutateAsync({
          agent: genie,
          updateData: { voice_bridge_enabled: false },
        });
      }

      await onOperationComplete?.();
    } catch (error) {
      toast.error("Oops, an error occurred");
      console.error("Error releasing transfer phone number:", error);
    } finally {
      setReleasingTransferId(null);
    }
  };

  const handleProvisionEscalation = async (values: PhoneDestinationFormValues) => {
    try {
      await provisionEscalation({ ...values, agentId: genie.id });
      // The browser handoff should never be on without a number to dial, so
      // it comes on with the first one.
      await enableLiveHandoff();
      setShowEscalationForm(false);
      await onOperationComplete?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Oops, an error occurred");
      console.error("Error provisioning escalation number:", error);
    }
  };

  const handleUpdateEscalation = async (phoneNumberId: string, values: PhoneDestinationFormValues) => {
    try {
      await updateEscalation({
        phoneNumberId,
        targetNumber: values.phoneNumber,
        friendlyName: values.friendlyName,
        description: values.description,
      });
      setEditingEscalationId(null);
      await onOperationComplete?.();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Oops, an error occurred");
      console.error("Error updating escalation number:", error);
    }
  };

  const handleToggleVoiceBridge = async (enabled: boolean) => {
    // Turning it on with no number at all: there is nothing to dial, so just
    // reveal the form instead of flipping the agent field. It gets flipped
    // once a number is actually provisioned (handleProvisionEscalation).
    if (enabled && !hasDestinations) {
      setShowEscalationForm(true);
      return;
    }

    setIsTogglingVoiceBridge(true);
    try {
      await updateAgent.mutateAsync({
        agent: genie,
        updateData: { voice_bridge_enabled: enabled },
      });
      await onOperationComplete?.();
    } catch (error) {
      toast.error("Oops, an error occurred");
      console.error("Error toggling voice bridge:", error);
    } finally {
      setIsTogglingVoiceBridge(false);
    }
  };

  const handleToggleShowButton = async (show: boolean) => {
    setIsTogglingShowButton(true);
    try {
      await updateAgent.mutateAsync({
        agent: genie,
        updateData: { voice_bridge_show_button: show },
      });
      await onOperationComplete?.();
    } catch (error) {
      toast.error("Oops, an error occurred");
      console.error("Error toggling voice bridge button visibility:", error);
    } finally {
      setIsTogglingShowButton(false);
    }
  };

  const handleProvision = async (params: BilledProvisionParams) => {
    setIsLoadingModal(true);
    const provisioned = await provisionWithBilling(params);
    setIsLoadingModal(false);
    if (!provisioned) return;

    setShowProvisionForm(false);
    if (forwardingIntent) setShowForwarding(true);
    await onOperationComplete?.();
  };

  type ExistingNumberParams =
    | { mode: "twilio"; phoneNumber: string; friendlyName?: string; contactEmail: string }
    | { mode: "port"; phoneNumber: string; friendlyName: string; countryCode: string; businessName: string; authorizedRepresentative: string; contactEmail: string; accountNumber?: string; currentCarrier?: string; billingAddress?: string; authorizationDocumentUrl?: string; latestBillUrl?: string; notes?: string };

  const handleExistingNumberRequest = async (params: ExistingNumberParams) => {
    try {
      await requestExistingNumberAttachment({
        ...params,
        source: params.mode === "twilio" ? "own_twilio" : "ported",
        friendlyName: params.friendlyName || `${genie.name} Number`,
        agentId: genie.id,
      });
      setShowExistingNumberForm(false);
      await onOperationComplete?.();
      toast.success(
        params.mode === "twilio"
          ? "Request submitted. We'll get in touch to connect your Twilio number."
          : "Port request submitted. We'll notify you when it is ready.",
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to add phone number");
      console.error("Error adding existing number:", error);
    }
  };

  const handleReleaseEscalation = async (phoneNumberId: string) => {
    if (!confirm("Are you sure you want to remove this escalation phone number?")) return;
    setReleasingEscalationId(phoneNumberId);
    try {
      await releaseEscalation(phoneNumberId);

      // The handoff falls back to the transfer numbers, so it only has to be
      // switched off once neither kind of number is left to dial.
      const hasRemainingDestination = escalationPhones.length > 1 || transferPhones.length > 0;
      if (genie.voice_bridge_enabled && !hasRemainingDestination) {
        await updateAgent.mutateAsync({
          agent: genie,
          updateData: { voice_bridge_enabled: false },
        });
      }

      await onOperationComplete?.();
    } catch (error) {
      toast.error("Oops, an error occurred");
      console.error("Error releasing escalation number:", error);
    } finally {
      setReleasingEscalationId(null);
    }
  };

  const handleRelease = async () => {
    if (!phoneNumber || !confirm("Are you sure you want to release this phone number? This action cannot be undone.")) {
      return;
    }
    try {
      await releaseWithBilling(phoneNumber.id, phoneNumber.phoneNumber);

      await onOperationComplete?.();
    } catch (error) {
      toast.error("Oops, an error occurred");
      console.error("Error releasing phone number:", error);
    }
  };

  const handleCall = async () => {
    if (!phoneNumber?.elevenlabsId) return;
    try {
      await makeCall(genie, phoneNumber.elevenlabsId);
    } catch (error) {
      toast.error("Oops, an error occurred");
      console.error("Error making call:", error);
    }
  };

  const handleClose = () => {
    setActiveTab("phone-numbers");
    setShowPhoneTypePrompt(false);
    setShowProvisionForm(false);
    setShowExistingNumberForm(false);
    setShowTransferForm(false);
    setEditingTransferId(null);
    setShowEscalationForm(false);
    setEditingEscalationId(null);
    setShowPhoneInfo(false);
    setShowTransferInfo(false);
    setShowDestinationGuide(false);
    setShowForwarding(false);
    setForwardingIntent(false);
    onClose();
  };

  const handleOpenPhoneCheck = (kind: PhoneCheckKind) => {
    handleClose();
    navigate(getPhoneCheckPath(genie.id, kind));
  };

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Phone Management"
      description={`Manage phone numbers and calls for ${genie.name}`}
      preventOutsideClick={isLoadingModal}
      className="sm:!max-w-[50vw]"
    >
      {isLoadingModal && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: brand }}></div>
            <p className="text-gray-700 font-medium">Setting up your phone number...</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setActiveTab("phone-numbers")}
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "phone-numbers" ? "bg-white shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "phone-numbers" ? { color: brand } : {}}
          >
            <Phone className="h-4 w-4 mr-2" />
            Phone Number
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("escalation")}
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "escalation" ? "bg-white shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "escalation" ? { color: brand } : {}}
          >
            <PhoneForwarded className="h-4 w-4 mr-2" />
            Escalation
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("call-logs")}
            className={`flex-1 flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === "call-logs" ? "bg-white shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "call-logs" ? { color: brand } : {}}
          >
            <PhoneCall className="h-4 w-4 mr-2" />
            Call Logs
          </button>
        </div>

        {activeTab === "phone-numbers" && (
          <>
            {/* ── Main Phone Number ── */}
            <div>
              {/* Section header — always visible */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium text-gray-900">Phone Number</h3>
                  <div className="relative">
                    <button
                      onClick={() => setShowPhoneInfo(!showPhoneInfo)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Phone number info"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                    {showPhoneInfo && (
                      <div
                        className="absolute left-0 top-6 z-10 w-64 rounded-lg p-3 text-xs"
                        style={{ backgroundColor: `${brand}10`, border: `1px solid ${brand}30`, color: brand }}
                      >
                        The number your genie uses to receive or make calls. Customers can call this number directly to
                        speak with your genie.
                      </div>
                    )}
                  </div>
                </div>
                {!phoneNumber && !showPhoneTypePrompt && !showProvisionForm && !showExistingNumberForm && (
                  <button
                    onClick={() => (canAddPhone ? setShowPhoneTypePrompt(true) : navigate("/pricing"))}
                    disabled={false}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
                    style={{ color: brand }}
                    title={!canAddPhone ? "Purchase a subscription to add a phone number" : ""}
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                )}
              </div>

              {/* Type-choice prompt — forwarding leads, because it is the only
                  route that gets an existing business number answered today.
                  Porting states its real timeline here, at the moment of choice,
                  not after an LOA has been filled in. */}
              {!phoneNumber && showPhoneTypePrompt && !showProvisionForm && !showExistingNumberForm && (
                <div className="mb-4 p-4 rounded-xl border border-gray-200 bg-gray-50 space-y-3">
                  <p className="text-sm font-medium text-gray-700">
                    What do you want to happen when someone calls your business?
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setForwardingIntent(true);
                        setShowPhoneTypePrompt(false);
                        setShowProvisionForm(true);
                      }}
                      className="flex flex-col items-start gap-1 px-4 py-3 rounded-lg border-2 bg-white text-left transition-colors"
                      style={{ borderColor: brand }}
                    >
                      <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        Keep my number and have my Genie answer it
                        <span
                          className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          style={{ backgroundColor: `${brand}18`, color: brand }}
                        >
                          Recommended
                        </span>
                      </span>
                      <span className="text-xs text-gray-500">
                        About three minutes on your own phone, working today. We give you a number to
                        forward to, then show you the exact code to dial. Reversible any time.
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setForwardingIntent(false);
                        setShowPhoneTypePrompt(false);
                        setShowProvisionForm(true);
                      }}
                      className="flex flex-col items-start gap-1 px-4 py-3 rounded-lg border-2 bg-white text-left transition-colors hover:border-current"
                      style={{ borderColor: `${brand}40` }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = brand;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = `${brand}40`;
                      }}
                    >
                      <span className="text-sm font-semibold text-gray-900">Just give me a new number</span>
                      <span className="text-xs text-gray-500">Live instantly. Nothing to set up on your side.</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setForwardingIntent(false);
                        setShowPhoneTypePrompt(false);
                        setShowExistingNumberForm(true);
                      }}
                      className="flex flex-col items-start gap-1 px-4 py-3 rounded-lg border-2 bg-white text-left transition-colors hover:border-current"
                      style={{ borderColor: `${brand}40` }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = brand;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = `${brand}40`;
                      }}
                    >
                      <span className="text-sm font-semibold text-gray-900">
                        Move my number to Help Genie permanently
                      </span>
                      <span className="text-xs text-gray-500">
                        Up to five business days. Our team works it with you, and calls keep going to
                        your current phone until it completes.
                      </span>
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPhoneTypePrompt(false)}
                    className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {/* Forms — rendered above transfer section */}
              {showProvisionForm && !phoneNumber && (
                <div className="mb-4">
                  {forwardingIntent && (
                    <div
                      className="mb-4 rounded-xl p-4"
                      style={{ backgroundColor: `${brand}10`, border: `1px solid ${brand}30` }}
                    >
                      <p className="text-sm font-semibold text-gray-900">Step 1 of 2</p>
                      <p className="mt-1 text-sm text-gray-600">
                        First we give your Genie a number of its own. Next we show you the code to
                        dial so your existing business number forwards to it. You keep your number.
                      </p>
                    </div>
                  )}
                  <ProvisioningForm
                    genieName={genie.name}
                    brand={brand}
                    onProvision={handleProvision}
                    onCancel={() => setShowProvisionForm(false)}
                    isProvisioning={isProvisioningBilled}
                  />
                </div>
              )}

              {showExistingNumberForm && !phoneNumber && (
                <div className="mb-4">
                  <ExistingNumberForm
                    genieName={genie.name}
                    brand={brand}
                    onSubmit={handleExistingNumberRequest}
                    onCancel={() => setShowExistingNumberForm(false)}
                    isSubmitting={isRequestingExistingNumberAttachment}
                  />
                </div>
              )}

              {/* Current number display */}
              {!showProvisionForm && !showExistingNumberForm && (
                <PhoneNumberDisplay
                  phoneNumber={phoneNumber}
                  isCalling={isCalling}
                  isReleasing={isReleasing}
                  onCall={handleCall}
                  onRelease={handleRelease}
                  canCall={phoneNumber?.status === "active" || phoneNumber?.status === "in-use"}
                />
              )}

              {/* Forwarding — the fastest route from "I have a business number"
                  to "my Genie answers it". Available as soon as a Help Genie
                  number exists, whether or not they arrived here intending it. */}
              {phoneNumber && !showProvisionForm && !showExistingNumberForm && (
                <div className="mt-4 rounded-xl border border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setShowForwarding((v) => !v)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
                  >
                    <span>
                      <span className="block text-sm font-semibold text-gray-900">
                        Have your existing business number answered too
                      </span>
                      <span className="block text-xs text-gray-500">
                        Forward it to this number. About three minutes, working today, reversible.
                      </span>
                    </span>
                    <span className="text-sm font-semibold" style={{ color: brand }}>
                      {showForwarding ? "Hide" : "Set up"}
                    </span>
                  </button>

                  {showForwarding && (
                    <div className="border-t border-gray-200 p-4">
                      <ForwardingSetup
                        genie={genie}
                        helpGenieNumber={phoneNumber.phoneNumber}
                        elevenlabsPhoneNumberId={phoneNumber.elevenlabsId ?? ""}
                        defaultCountry={genie.phone?.country_code === "AU" ? "AU" : "NZ"}
                      />
                    </div>
                  )}
                </div>
              )}

              {phoneNumber?.metadata?.source === "ported" && phoneNumber.status !== "active" && (
                <div className="mt-4 p-4 rounded-xl border-l-4 bg-amber-50 border-amber-400">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800 capitalize">
                        {phoneNumber.status.replace(/_/g, " ")}
                      </p>
                      <p className="text-sm text-amber-700 mt-0.5">
                        Your number is being transferred. Calls will route here once the port is complete — this can
                        take up to 5 business days.
                      </p>
                      {isInternalAdmin() && (
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/phone-numbers?id=${phoneNumber.recordId}`)}
                          className="mt-2 text-sm font-medium underline text-gray-500 hover:text-amber-900 transition-colors"
                        >
                          Hey admin! Manage porting →
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!phoneNumber &&
                !showPhoneTypePrompt &&
                !showProvisionForm &&
                !showExistingNumberForm &&
                !canAddPhone && (
                  <div
                    className="mt-4 p-4 rounded-lg"
                    style={{ backgroundColor: `${brand}10`, border: `1px solid ${brand}30` }}
                  >
                    <p className="text-sm mb-3" style={{ color: brand }}>
                      To add a phone number, you need to purchase a subscription first.
                    </p>
                    <button
                      onClick={() => navigate("/pricing")}
                      className="text-sm font-medium underline"
                      style={{ color: brand }}
                    >
                      View pricing plans →
                    </button>
                  </div>
                )}
            </div>

            {/* ── Transfer & escalation summary ── */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Transfer & Escalation Numbers</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    The people your Genie passes callers and website visitors on to.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTab("escalation")}
                  className="flex flex-shrink-0 items-center gap-1 px-3 py-1.5 min-h-[44px] text-sm font-medium rounded-lg transition-colors"
                  style={{ color: brand }}
                >
                  {hasDestinations ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {hasDestinations ? "Edit" : "Add"}
                </button>
              </div>

              {hasDestinations ? (
                <div className="space-y-4">
                  {destinationSummary.map((group) => (
                    <div key={group.label}>
                      <p className="text-sm font-semibold text-gray-700 mb-2">{group.label}</p>
                      {group.phones.length > 0 ? (
                        <div className="space-y-2">
                          {group.phones.map((phone) => (
                            <div
                              key={phone.id}
                              className="flex items-center justify-between gap-3 px-4 py-2.5 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <p className="min-w-0 truncate text-sm font-medium text-gray-900">
                                {phone.friendlyName || phone.phoneNumber}
                              </p>
                              {phone.friendlyName && (
                                <p className="flex-shrink-0 text-sm text-gray-500">{phone.phoneNumber}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">{group.emptyText}</p>
                      )}
                    </div>
                  ))}
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500">{callerHandoffSummary}</p>
                    <p className="text-xs text-gray-500">{visitorHandoffSummary}</p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  No numbers yet. Add one so your Genie can pass people on to someone on your team.
                </p>
              )}
            </div>
          </>
        )}

        {activeTab === "escalation" && (
          <>
            {/* ── Transfer vs escalation numbers ── */}
            <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              <div className="rounded-[10px] bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowDestinationGuide((shown) => !shown)}
                  aria-expanded={showDestinationGuide}
                  className="w-full min-h-[44px] px-4 py-2 flex items-center justify-between gap-3 text-left bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900">Transfer or escalation number: which one do I need?</span>
                  <span className="text-xs text-gray-500">{showDestinationGuide ? "Hide" : "Show"}</span>
                </button>
                {showDestinationGuide && (
                  <div className="px-4 py-3 border-t space-y-3">
                    <p className="text-sm text-gray-600">
                      Both are numbers of real people your Genie can pass someone on to. The difference is where your
                      customer is when it happens.
                    </p>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Transfer numbers: for phone callers</p>
                        <p className="mt-1 text-sm text-gray-600">
                          Your customer rang your Genie's phone number. When they need a person, the Genie transfers
                          the call to one of your transfer numbers.
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Escalation numbers: for website visitors</p>
                        <p className="mt-1 text-sm text-gray-600">
                          Your customer is talking to your Genie in their browser. When they need a person, a phone
                          call to one of your escalation numbers starts right there on the page.
                        </p>
                      </div>
                    </div>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
                      <li>You can add up to {MAX_PHONE_DESTINATIONS} of each.</li>
                      <li>
                        Give each number a description of when to use it, and your Genie picks the one that fits what
                        the customer needs.
                      </li>
                      <li>A number with no description is used when the customer simply asks for a person.</li>
                      <li>
                        Most businesses use the same numbers for both: add transfer numbers and switch on Live Handoff
                        from Browser, and website visitors are connected to your transfer numbers too.
                      </li>
                      <li>Add escalation numbers only if website visitors should reach different people than callers.</li>
                      <li>Transfer numbers need your Genie to have its own phone number first.</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* ── Transfer Phone Numbers ── */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-medium text-gray-900">Transfer Phone Numbers</h3>
                  <div className="relative">
                    <button
                      onClick={() => setShowTransferInfo(!showTransferInfo)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Transfer phone numbers info"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                    {showTransferInfo && (
                      <div
                        className="absolute left-0 top-6 z-10 w-64 rounded-lg p-3 text-xs"
                        style={{ backgroundColor: `${brand}10`, border: `1px solid ${brand}30`, color: brand }}
                      >
                        Phone calls only. The Genie transfers the caller to the number whose description matches what
                        they need, and confirms with them first. Add up to {MAX_PHONE_DESTINATIONS} numbers. Number transfer
                        restrictions vary by country — verify your number is available in your region.
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isTransferConfigured && transferPhones.length > 0 && !showTransferForm && !editingTransferId && (
                    <button
                      onClick={() => handleOpenPhoneCheck("transfer")}
                      className="flex items-center gap-1 px-3 py-1.5 min-h-[44px] text-sm font-medium rounded-lg transition-colors"
                      style={{ color: brand }}
                      title="Check that your Genie transfers callers to the right number"
                    >
                      <FlaskConical className="h-4 w-4" />
                      Test
                    </button>
                  )}
                {!showTransferForm && !editingTransferId && canAddTransfer && (
                  <button
                    onClick={() => setShowTransferForm(true)}
                    disabled={!canAddPhone || !phoneNumber}
                    className="flex items-center gap-1 px-3 py-1.5 min-h-[44px] text-sm font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: canAddPhone && phoneNumber ? brand : "#9CA3AF" }}
                    title={
                      !phoneNumber
                        ? "Set up a phone number first before adding a transfer number"
                        : !canAddPhone
                          ? "Purchase a subscription to add a transfer phone number"
                          : ""
                    }
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                )}
                </div>
              </div>

              {transferPhones.length > 0 && (
                <div className="space-y-3">
                  {transferPhones.map((transfer) =>
                    editingTransferId === transfer.id ? (
                      <PhoneDestinationForm
                        variant="transfer"
                        key={transfer.id}
                        brand={brand}
                        initialValues={{
                          phoneNumber: transfer.phoneNumber,
                          friendlyName: transfer.friendlyName,
                          description: transfer.description,
                        }}
                        onSubmit={(values) => handleUpdateTransfer(transfer.id, values)}
                        onCancel={() => setEditingTransferId(null)}
                        isSubmitting={isUpdatingTransfer}
                      />
                    ) : (
                      <div key={transfer.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900">{transfer.friendlyName || transfer.phoneNumber}</p>
                            <p className="text-sm text-gray-500">{transfer.phoneNumber}</p>
                            <p className="text-xs text-gray-500 mt-2">
                              {transfer.description || "Used when a caller asks to speak to a person."}
                            </p>
                          </div>
                          <div className="flex flex-shrink-0 gap-1">
                            <button
                              onClick={() => {
                                setShowTransferForm(false);
                                setEditingTransferId(transfer.id);
                              }}
                              disabled={!!releasingTransferId}
                              className="p-2.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors disabled:opacity-50"
                              title="Edit transfer number"
                              aria-label="Edit transfer number"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleReleaseTransfer(transfer.id)}
                              disabled={!!releasingTransferId}
                              className="p-2.5 text-gray-400 hover:text-red-500 rounded-lg transition-colors disabled:opacity-50"
                              title="Release transfer number"
                              aria-label="Release transfer number"
                            >
                              {releasingTransferId === transfer.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ),
                  )}

                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-gray-400">
                      {transferPhones.length}/{MAX_PHONE_DESTINATIONS} transfer numbers
                    </p>
                    {isTransferConfigured ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full text-center">
                        ✓ Configured
                      </span>
                    ) : (
                      <button
                        onClick={handleResyncTransfers}
                        disabled={isUpdatingTransfer}
                        className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full text-center hover:bg-amber-200 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        {isUpdatingTransfer ? "Updating..." : "⚠ Not Configured"}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {transferPhones.length === 0 && !showTransferForm && !phoneNumber && (
                <p className="text-xs text-gray-400 mt-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab("phone-numbers")}
                    className="font-medium underline"
                    style={{ color: brand }}
                  >
                    Set up a phone number
                  </button>{" "}
                  first before adding a transfer number.
                </p>
              )}

              {transferPhones.length === 0 && !showTransferForm && phoneNumber && !canAddPhone && (
                <div
                  className="mt-3 p-3 rounded-lg"
                  style={{ backgroundColor: `${brand}10`, border: `1px solid ${brand}30` }}
                >
                  <p className="text-xs mb-2" style={{ color: brand }}>
                    To add a transfer phone number, you need to purchase a subscription first.
                  </p>
                  <button
                    onClick={() => navigate("/pricing")}
                    className="text-xs font-medium underline"
                    style={{ color: brand }}
                  >
                    View pricing plans →
                  </button>
                </div>
              )}

              {showTransferForm && canAddTransfer && (
                <PhoneDestinationForm
                  variant="transfer"
                  brand={brand}
                  offerLiveHandoff={!genie.voice_bridge_enabled && escalationPhones.length === 0}
                  onSubmit={handleProvisionTransfer}
                  onCancel={() => setShowTransferForm(false)}
                  isSubmitting={isProvisioningTransfer}
                />
              )}
            </div>

            {/* ── Live Voice Handoff (browser → human) ── */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Live Handoff from Browser</h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Let browser visitors ask this Genie to connect them to a live phone call.
                  </p>
                  {genie.voice_bridge_enabled && (
                    <p className="text-xs text-gray-700 mt-1.5">{visitorHandoffSummary}</p>
                  )}
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={genie.voice_bridge_enabled}
                  aria-busy={isTogglingVoiceBridge}
                  onClick={() => handleToggleVoiceBridge(!genie.voice_bridge_enabled)}
                  disabled={isTogglingVoiceBridge}
                  className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed"
                  style={{ backgroundColor: genie.voice_bridge_enabled ? brand : "#D1D5DB" }}
                >
                  {isTogglingVoiceBridge ? (
                    <Loader2 className="absolute inset-0 m-auto h-3.5 w-3.5 animate-spin text-white" />
                  ) : (
                    <span
                      className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                      style={{ transform: genie.voice_bridge_enabled ? "translateX(22px)" : "translateX(4px)" }}
                    />
                  )}
                </button>
              </div>

              {(genie.voice_bridge_enabled || showEscalationForm) && (
                <>
                  {genie.voice_bridge_enabled && (
                  <div className="flex items-center justify-between mb-4 pt-2 border-t border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Show "Talk to a human" button</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Turn off to allow only the AI-triggered handoff, with no visible button.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={genie.voice_bridge_show_button !== false}
                      aria-busy={isTogglingShowButton}
                      onClick={() => handleToggleShowButton(genie.voice_bridge_show_button === false)}
                      disabled={isTogglingShowButton}
                      className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed"
                      style={{ backgroundColor: genie.voice_bridge_show_button !== false ? brand : "#D1D5DB" }}
                    >
                      {isTogglingShowButton ? (
                        <Loader2 className="absolute inset-0 m-auto h-3.5 w-3.5 animate-spin text-white" />
                      ) : (
                        <span
                          className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
                          style={{
                            transform: genie.voice_bridge_show_button !== false ? "translateX(22px)" : "translateX(4px)",
                          }}
                        />
                      )}
                    </button>
                  </div>
                  )}

                  {escalationPhones.length > 0 && (
                    <div className="space-y-3">
                      {escalationPhones.map((escalation) =>
                        editingEscalationId === escalation.id ? (
                          <PhoneDestinationForm
                            key={escalation.id}
                            variant="escalation"
                            brand={brand}
                            initialValues={{
                              phoneNumber: escalation.phoneNumber,
                              friendlyName: escalation.friendlyName,
                              description: escalation.description,
                            }}
                            onSubmit={(values) => handleUpdateEscalation(escalation.id, values)}
                            onCancel={() => setEditingEscalationId(null)}
                            isSubmitting={isUpdatingEscalation}
                          />
                        ) : (
                          <div
                            key={escalation.id}
                            className="p-4 rounded-lg border border-l-4"
                            style={{ backgroundColor: `${brand}0D`, borderColor: `${brand}40`, borderLeftColor: brand }}
                          >
                            <div className="flex justify-between items-start gap-3">
                              <div className="min-w-0">
                                <p className="flex items-center gap-1.5 font-medium text-gray-900">
                                  <PhoneCall className="h-4 w-4 flex-shrink-0" style={{ color: brand }} />
                                  {escalation.friendlyName || escalation.phoneNumber}
                                </p>
                                <p className="text-sm text-gray-500">{escalation.phoneNumber}</p>
                                <p className="text-xs text-gray-500 mt-2">
                                  {escalation.description || "Used when nothing more specific matches."}
                                </p>
                              </div>
                              <div className="flex flex-shrink-0 gap-1">
                                <button
                                  onClick={() => {
                                    setShowEscalationForm(false);
                                    setEditingEscalationId(escalation.id);
                                  }}
                                  disabled={!!releasingEscalationId}
                                  className="p-2.5 text-gray-400 hover:text-gray-600 rounded-lg transition-colors disabled:opacity-50"
                                  title="Edit escalation number"
                                  aria-label="Edit escalation number"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleReleaseEscalation(escalation.id)}
                                  disabled={!!releasingEscalationId}
                                  className="p-2.5 text-gray-400 hover:text-red-500 rounded-lg transition-colors disabled:opacity-50"
                                  title="Remove escalation number"
                                  aria-label="Remove escalation number"
                                >
                                  {releasingEscalationId === escalation.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        ),
                      )}
                      <p className="text-xs text-gray-400">
                        {escalationPhones.length}/{MAX_PHONE_DESTINATIONS} escalation numbers
                        {escalationPhones.length > 1 && " — visitors are connected to the one that matches their conversation."}
                      </p>
                    </div>
                  )}

                  {!showEscalationForm && !editingEscalationId && (
                    <div className="flex flex-wrap items-center gap-1">
                      {canAddEscalation && (
                        <button
                          onClick={() => setShowEscalationForm(true)}
                          className="flex items-center gap-1 px-3 py-1.5 min-h-[44px] text-sm font-medium rounded-lg transition-colors"
                          style={{ color: brand }}
                        >
                          <Plus className="h-4 w-4" />
                          Add escalation number
                        </button>
                      )}
                      {genie.voice_bridge_enabled && hasDestinations && (
                        <button
                          onClick={() => handleOpenPhoneCheck("handoff")}
                          className="flex items-center gap-1 px-3 py-1.5 min-h-[44px] text-sm font-medium rounded-lg transition-colors"
                          style={{ color: brand }}
                          title="Check that your Genie hands visitors over at the right moments"
                        >
                          <FlaskConical className="h-4 w-4" />
                          Test
                        </button>
                      )}
                    </div>
                  )}

                  {escalationPhones.length === 0 && !showEscalationForm && (
                    <p className="text-xs text-gray-400 mt-1">
                      {transferPhones.length > 1
                        ? "No escalation numbers, so website visitors are connected to your transfer numbers, to the one that matches their conversation."
                        : transferPhone
                          ? `No escalation numbers, so website visitors are connected to your transfer number (${transferPhone.phoneNumber}).`
                          : "Add an escalation number, or set up a transfer number above — either works as the handoff target."}
                    </p>
                  )}

                  {showEscalationForm && canAddEscalation && (
                    <PhoneDestinationForm
                      variant="escalation"
                      brand={brand}
                      onSubmit={handleProvisionEscalation}
                      onCancel={() => setShowEscalationForm(false)}
                      isSubmitting={isProvisioningEscalation}
                    />
                  )}
                </>
              )}
            </div>
          </>
        )}

        {activeTab === "call-logs" && <CallLogs agentId={genie.id} />}
      </div>
    </CoreDrawer>
  );
};

export default PhoneNumberModal;
